import React, { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { useCanvasStore, type Item } from '@/stores/canvasStore';
import { fallbackLinkContent, fetchLinkPreview } from '@/lib/linkPreview';
import { deleteDiscoverItem } from '@/lib/discoverDelete';
import { requestUpload } from '@/lib/uploadRequests';
import { requestSubjectBackfill } from '@/lib/library/subjectTagging';
import { DropZone } from '@/components/upload/DropZone';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LibraryAccountSheet } from './LibraryAccountSheet';
import { QuickSaveSetup } from '@/components/settings/QuickSaveSettings';
import { PartnrConfirmDialog } from '@/components/partnr/PartnrConfirmDialog';
import { MobileLibrary } from './MobileLibrary';
import { canPromptInstall, installPlatform, isStandalone, promptInstall, subscribeInstall } from '@/lib/appInstall';
import { useSupabaseComments } from './useSupabaseComments';
import type { LibraryActions } from './LibraryContext';

/**
 * The mobile Library wired to the real store. Canvas.tsx mounts this on
 * phones in place of the Wall; items and boards are fetched there on mount.
 * Uploads go through the Wall's DropZone (requestUpload), so photos picked
 * on the phone take the same path as a drop on desktop.
 */
export function MobileLibraryContainer() {
  const allItems = useCanvasStore((s) => s.allItems);
  const boards = useCanvasStore((s) => s.boards);
  // The library's own fetch (fetchAllItems), not the canvas board's isLoading
  const allItemsLoaded = useCanvasStore((s) => s.allItemsLoaded);
  const createItem = useCanvasStore((s) => s.createItem);
  const updateItem = useCanvasStore((s) => s.updateItem);
  const createBoard = useCanvasStore((s) => s.createBoard);
  const fetchAllItems = useCanvasStore((s) => s.fetchAllItems);

  const refresh = useCallback(() => fetchAllItems(), [fetchAllItems]);
  const comments = useSupabaseComments();
  // Links saved a moment ago whose preview is still on its way
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  const setPendingId = useCallback((id: string, on: boolean) => {
    setPending((prev) => {
      if (prev.has(id) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Coming back to the app (from Instagram's share sheet, say) shows what was
  // saved meanwhile. At most every 5s, so flicking between apps doesn't hammer it.
  useEffect(() => {
    let last = Date.now();
    const onVisible = () => {
      if (document.hidden || Date.now() - last < 5000) return;
      last = Date.now();
      void fetchAllItems();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, [fetchAllItems]);

  // Subject labels for items saved before tagging existed: at most once a day per device
  // The refetch is a store call, safe after unmount, so it is not cancelled
  // (strict mode's second mount is throttled and would never refetch).
  useEffect(() => {
    requestSubjectBackfill()
      .then((tagged) => {
        if (tagged > 0) fetchAllItems();
      })
      .catch((e) => console.warn('[MobileLibrary] Subject backfill failed', e));
  }, [fetchAllItems]);

  // Previews for links saved without one (a bulk Instagram import has none):
  // fetched a few at a time while the app is open, newest first, free sources
  // only. Pauses when the app goes to the background; picks up next time.
  const hasItems = allItems.length > 0;
  useEffect(() => {
    if (!hasItems) return;
    let controller = new AbortController();
    let running = false;
    const start = async () => {
      if (running || document.hidden) return;
      running = true;
      try {
        const [refreshMod, backfillMod] = await Promise.all([import('@/lib/linkPreviewRefresh'), import('@/lib/library/previewBackfill')]);
        await backfillMod.runPreviewBackfill(useCanvasStore.getState().allItems, {
          claim: (item) => refreshMod.claimBackgroundRefresh(item, 'free'),
          refresh: (item) => refreshMod.refreshLinkPreview(refreshMod.latestItem(item)),
          signal: controller.signal,
        });
      } catch (e) {
        console.warn('[MobileLibrary] Preview backfill stopped', e);
      } finally {
        running = false;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        controller.abort();
        controller = new AbortController();
      } else {
        void start();
      }
    };
    void start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      controller.abort();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [hasItems]);

  const actions = useMemo<LibraryActions>(
    () => ({
      deleteItem: async (item: Item) => {
        const deleted = await deleteDiscoverItem(item);
        if (deleted) await refresh();
        return deleted;
      },
      moveToBoard: async (item: Item, boardId: string) => {
        await updateItem(item.id, { board_id: boardId });
        await refresh();
      },
      // A space made on the phone lives in Home: the board's parent is Home and
      // its folder sits on the Home canvas, right of what is there, as a folder
      // made on desktop would
      createSpace: async (name: string) => {
        const { boards: all, allItems: items } = useCanvasStore.getState();
        const home = all.find((b) => b.parent_id === null) ?? null;
        const board = await createBoard(name, home?.id ?? null);
        if (!board || !home) return board;
        const onHome = items.filter((i) => i.board_id === home.id);
        const right = onHome.reduce((x, i) => Math.max(x, (i.position_x ?? 0) + (i.width ?? 0)), 0);
        const top = onHome.length ? Math.min(...onHome.map((i) => i.position_y ?? 0)) : 0;
        await createItem({
          board_id: home.id,
          item_type: 'board_link',
          position_x: onHome.length ? right + 40 : 0,
          position_y: top,
          width: 200,
          height: 150,
          content: { linked_board_id: board.id, name: board.name, color: board.icon_color },
        });
        return board;
      },
      // Saved at once from what the URL says (site, platform, a title from the
      // path); the preview (picture, caption, screenshot) is fetched after and
      // written into the item, which can take seconds. createItem puts the item
      // in the store itself, so there is no refetch of the whole library.
      addLink: async (url: string) => {
        try {
          const quick = fallbackLinkContent(url);
          const item = await createItem({
            board_id: null,
            item_type: 'link',
            width: 280,
            height: 200,
            content: { ...quick, title: quick.title || url, needsBoard: true },
          });
          if (!item) throw new Error('createItem returned nothing');
          setPendingId(item.id, true);
          void fetchLinkPreview(url)
            .then(async (content) => {
              const latest = useCanvasStore.getState().allItems.find((i) => i.id === item.id);
              if (!latest) return; // deleted meanwhile
              const previous = (latest.content ?? {}) as Record<string, unknown>;
              await updateItem(item.id, { content: { ...previous, ...content, title: content.title || url, needsBoard: previous.needsBoard ?? true } as unknown as Item['content'] });
            })
            .catch((err) => console.warn('[MobileLibrary] preview after save failed', err))
            .finally(() => setPendingId(item.id, false));
          return item;
        } catch (err) {
          console.error('[MobileLibrary] addLink failed', err);
          toast.error('Could not save that link');
          return null;
        }
      },
      addNote: async (text: string) => {
        try {
          const item = await createItem({
            board_id: null,
            item_type: 'text_card',
            width: 280,
            height: 200,
            content: { body: text, title: '', needsBoard: true, libraryNote: true },
          });
          return item;
        } catch (err) {
          console.error('[MobileLibrary] addNote failed', err);
          toast.error('Could not save that note');
          return null;
        }
      },
      // The desktop text card's own write: updateItem merges into the item's
      // content (libraryNote, title and the rest stay) and queues the save
      updateNote: async (item: Item, body: string) => {
        try {
          await updateItem(item.id, { content: { body } as unknown as Item['content'] });
          // Saved as the app goes to the background: send it now, the queue's timer may not get to run
          if (document.visibilityState === 'hidden') {
            const { flush } = await import('@/lib/mutationQueue');
            void flush();
          }
        } catch (err) {
          console.error('[MobileLibrary] updateNote failed', err);
          toast.error('Could not save that note');
        }
      },
      addFiles: async (files: File[]) => {
        requestUpload(files);
      },
      refresh,
      // The desktop cards' own queue: two at a time, once per item per mode per session
      refreshPreview: (item, mode) => {
        void import('@/lib/linkPreviewRefresh').then((m) =>
          mode === 'creative' ? m.scheduleCreativeRefresh(item) : m.scheduleBackgroundRefresh(item, mode),
        );
      },
    }),
    [createItem, createBoard, updateItem, refresh, setPendingId],
  );

  return (
    <DropZone mode="wall" onFilesUploaded={refresh}>
      <MobileLibrary items={allItems} boards={boards} actions={actions} comments={comments} pending={pending} isLoading={!allItemsLoaded && allItems.length === 0} accountSlot={<LibraryAccount />} />
      {/* Delete confirms (deleteDiscoverItem) */}
      <PartnrConfirmDialog />
    </DropZone>
  );
}

/** The header's account button and sheet, wired to auth and the store's dark mode. */
function LibraryAccount() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const isDarkMode = useCanvasStore((s) => s.isDarkMode);
  const toggleDarkMode = useCanvasStore((s) => s.toggleDarkMode);
  const canPrompt = useSyncExternalStore(subscribeInstall, canPromptInstall, () => false);
  const app = useMemo(() => ({ installed: isStandalone(), canPrompt, platform: installPlatform(), onInstall: promptInstall }), [canPrompt]);
  if (!user) return null;
  const meta = user.user_metadata ?? {};
  return (
    <LibraryAccountSheet
      name={meta.full_name || meta.name || null}
      email={user.email ?? null}
      avatarUrl={meta.avatar_url || meta.picture || null}
      isDarkMode={isDarkMode}
      onToggleDarkMode={toggleDarkMode}
      app={app}
      quickSave={<QuickSaveSetup active />}
      onSignOut={async () => {
        await signOut();
        navigate('/');
      }}
    />
  );
}
